import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Card, Input, OtpInput } from '@/components/ui';
import { Layout } from '@/components/layout';
import { mfaService } from '@/services/auth/MfaService';
import { useMfaStore } from '@/stores/mfaStore';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/hooks/useTheme';
import { apiService } from '@/services';
import { BaseApiError } from '@/services/shared/BaseApiError';

const OtpPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const {
    mfaRequired,
    sendStatus,
    verifyStatus,
    error,
    otpMetadata,
    setMfaRequired,
    setSendStatus,
    setVerifyStatus,
    setOtpMetadata,
  } = useMfaStore();

  const OTP_LENGTH = 6;
  const AUTO_SEND_STORAGE_KEY = 'mfa:autoSendOnceDone:v1';
  const [code, setCode] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [userEmail, setUserEmail] = useState<string>('');
  const [showSpamHint, setShowSpamHint] = useState<boolean>(false);
  const verifyAttemptRef = useRef<number>(0);

  const formatErrorMessage = (err: unknown, fallback: string): string => {
    if (err instanceof BaseApiError) {
      if (typeof err.message === 'string' && err.message.trim()) {
        return err.message;
      }
    }
    if (err && typeof err === 'object' && 'error' in err) {
      const value = (err as { error?: unknown }).error;
      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }
    if (err instanceof Error && typeof err.message === 'string' && err.message.trim()) {
      return err.message;
    }
    return fallback;
  };

  // Fetch user email from API if not available from auth context
  useEffect(() => {
    const fetchUserEmail = async () => {
      // Use email from auth context if available
      if (user?.email) {
        setUserEmail(user.email);
        return;
      }

      // Otherwise fetch from API (which doesn't require MFA)
      try {
        const currentUser = await apiService.user.getCurrentUser();
        if (currentUser?.email) {
          setUserEmail(currentUser.email);
        }
      } catch (err) {
        // Silent failure - email field will just be empty
        console.warn('Failed to fetch user email:', err);
      }
    };

    fetchUserEmail();
  }, [user?.email]);

  useEffect(() => {
    const expiresAt = otpMetadata?.cooldownExpiresAt;
    if (!expiresAt) {
      setCooldownSeconds(0);
      return undefined;
    }

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setCooldownSeconds(remaining);
    };

    tick();
    const interval = window.setInterval(tick, 500);
    return () => window.clearInterval(interval);
  }, [otpMetadata?.cooldownExpiresAt]);

  useEffect(() => {
    // If MFA is already satisfied, redirect to dashboard
    if (!mfaRequired && verifyStatus === 'success') {
      navigate('/dashboard', { replace: true });
    }
  }, [mfaRequired, verifyStatus, navigate]);

  const handleSend = async () => {
    // Reset OTP input + verification state whenever a new code is requested
    setCode('');
    setVerifyStatus('idle', null);
    verifyAttemptRef.current += 1; // invalidate any in-flight verify requests
    setShowSpamHint(true);

    setSendStatus('pending');
    setSuccessMessage('');
    try {
      const response = await mfaService.sendOtp();
      setOtpMetadata({
        deviceId: response.device_id,
        preAuthSessionId: response.pre_auth_session_id,
        expiresIn: response.expires_in,
        resendAvailableIn: response.resend_available_in,
      });
      setSendStatus('success');
      setMfaRequired(true);
    } catch (err) {
      const retryAfter =
        err instanceof BaseApiError
          ? Number(
              err.getFieldError('retry_after') ||
                (typeof err.message === 'string' ? err.message.match(/\d+/)?.[0] : undefined),
            )
          : undefined;
      const message = formatErrorMessage(err, 'Failed to send OTP');
      setSendStatus('error', message);
      if (Number.isFinite(retryAfter)) {
        const seconds = Number(retryAfter);
        setOtpMetadata({
          resendAvailableIn: seconds,
          cooldownExpiresAt: Date.now() + seconds * 1000,
        });
      }
    }
  };

  const verifyCode = async (rawCode: string) => {
    const normalized = rawCode.replace(/\D/g, '');
    if (normalized.length !== OTP_LENGTH) {
      setVerifyStatus('error', 'Enter the 6-digit code from your email');
      return;
    }

    const attemptId = verifyAttemptRef.current + 1;
    verifyAttemptRef.current = attemptId;
    setVerifyStatus('pending');
    setSuccessMessage('');
    try {
      await mfaService.verifyOtp(normalized);
      if (verifyAttemptRef.current !== attemptId) {
        return;
      }
      setVerifyStatus('success');
      setMfaRequired(false);
      setSuccessMessage('MFA complete. Redirecting...');
      setOtpMetadata(null);
      setCode('');
      // Reset the one-time auto-send flag after successful verification
      // so future MFA challenges can auto-send again.
      try {
        window.localStorage?.removeItem(AUTO_SEND_STORAGE_KEY);
      } catch {
        // Ignore storage errors
      }
      setTimeout(() => navigate('/dashboard', { replace: true }), 800);
    } catch (err) {
      if (verifyAttemptRef.current !== attemptId) {
        return;
      }
      const baseError = err instanceof BaseApiError ? err : null;
      const message = formatErrorMessage(
        baseError?.message ? baseError : err,
        'Failed to verify code',
      );

      // Always surface the error message and re-enable the verify button
      setVerifyStatus('error', message || 'Failed to verify code');

      const retryAfter =
        baseError?.getFieldError('retry_after') ||
        (err instanceof BaseApiError
          ? typeof err.message === 'string'
            ? err.message.match(/\d+/)?.[0]
            : undefined
          : undefined);

      if (Number.isFinite(Number(retryAfter))) {
        const seconds = Number(retryAfter);
        setOtpMetadata({
          resendAvailableIn: seconds,
          cooldownExpiresAt: Date.now() + seconds * 1000,
        });
      }
    }
  };

  const handleVerify = async () => verifyCode(code);

  // Auto-send the code on the first-ever load of this page (persisted across refresh/back)
  useEffect(() => {
    try {
      const alreadyAutoSent = window.localStorage?.getItem(AUTO_SEND_STORAGE_KEY) === '1';
      if (alreadyAutoSent) {
        return;
      }
      window.localStorage?.setItem(AUTO_SEND_STORAGE_KEY, '1');
    } catch {
      // If storage is blocked, fall back to not auto-sending repeatedly
      return;
    }

    void handleSend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-verify as soon as all 6 OTP fields are filled
  useEffect(() => {
    const normalized = code.replace(/\D/g, '');
    if (normalized.length !== OTP_LENGTH) {
      return;
    }
    if (verifyStatus === 'pending' || verifyStatus === 'success') {
      return;
    }
    void verifyCode(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <Layout showFooter={false}>
      <div className="flex items-center justify-center py-12 px-4">
        <Card
          className={`max-w-lg w-full p-8 space-y-6 ${
            isDark ? 'bg-secondary-800 border-secondary-700' : 'bg-white'
          }`}
        >
          <div className="space-y-2">
            <h1
              className={`text-2xl font-semibold ${
                isDark ? 'text-secondary-100' : 'text-secondary-900'
              }`}
            >
              Authentication Challenge
            </h1>
            <p className={isDark ? 'text-secondary-400' : 'text-secondary-600'}>
              Enter the one-time code sent to your email to continue.
            </p>
          </div>

          <div className="space-y-3">
            <Input
              type="email"
              value={userEmail}
              readOnly
              className={`w-full cursor-not-allowed ${
                isDark
                  ? 'bg-secondary-700 text-secondary-300 border-secondary-600'
                  : 'bg-secondary-100 text-secondary-600'
              }`}
              aria-label="Email address"
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleSend}
              disabled={sendStatus === 'pending' || cooldownSeconds > 0}
              className="whitespace-nowrap"
            >
              {sendStatus === 'pending'
                ? 'Sending...'
                : otpMetadata
                  ? cooldownSeconds > 0
                    ? `Resend in ${cooldownSeconds}s`
                    : 'Resend Code'
                  : 'Send Code'}
            </Button>
          </div>
          {showSpamHint ? (
            <p className={`text-sm ${isDark ? 'text-secondary-400' : 'text-secondary-500'}`}>
              Didn’t receive the email? Check spam/junk.
            </p>
          ) : null}

          <div className="space-y-2">
            <label
              className={`text-sm font-medium ${
                isDark ? 'text-secondary-300' : 'text-secondary-700'
              }`}
            >
              Verification code
            </label>
            <OtpInput
              value={code}
              onChange={setCode}
              length={OTP_LENGTH}
              disabled={verifyStatus === 'pending'}
              aria-label="Verification code"
            />
            <div className="flex justify-end">
              <Button onClick={handleVerify} disabled={verifyStatus === 'pending'}>
                {verifyStatus === 'pending' ? 'Verifying...' : 'Verify Code'}
              </Button>
            </div>
            {verifyStatus === 'error' ? (
              <div
                className={`rounded-md p-3 ${
                  isDark ? 'bg-secondary-700/60' : 'bg-secondary-50'
                }`}
              >
                {error ? (
                  <p className={`text-sm ${isDark ? 'text-red-300' : 'text-red-600'}`}>
                    {error}
                  </p>
                ) : null}
                <p className={`text-sm ${isDark ? 'text-secondary-300' : 'text-secondary-700'}`}>
                  If verification fails, request a new code and make sure you enter the{' '}
                  <span className="font-semibold">latest</span> code from your email.
                </p>
                <div className="mt-2 flex justify-end">
                  <Button
                    variant="ghost"
                    onClick={handleSend}
                    disabled={sendStatus === 'pending' || cooldownSeconds > 0}
                    className="whitespace-nowrap"
                  >
                    Send new code
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
          {successMessage ? (
            <p className={`text-sm ${isDark ? 'text-green-400' : 'text-green-700'}`}>
              {successMessage}
            </p>
          ) : null}
        </Card>
      </div>
    </Layout>
  );
};

export default OtpPage;
